import { DimensionsContext, type Size } from '@contexts/dimensions'
import { useIsClientSide } from '@hooks/use-is-client-side'
import React, {
  type FC,
  type ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReactPlayer from 'react-player'
import { type BaseReactPlayerProps } from 'react-player/base'

import { ControlsDuration } from './controls/controls-duration'
import { ControlsPlay } from './controls/controls-play'
import { ControlsTimeIndicator } from './controls/controls-time-indicator'
import {
  MEDIA_FORMAT,
  UnsupportedMediaMessage,
} from './unsupported-media-message'

type Props = {
  url: string
  preview?: ReactElement
  options: {
    format?: string
    width?: string | number
    height?: string | number
  }
}

const getPlayerResolution = (
  videoResolution?: Size,
  containerDimensions?: Size
): number | undefined => {
  const containerWidth = containerDimensions?.width || 0
  const videoWidth = videoResolution?.width || 0
  const resolution = videoWidth < containerWidth ? videoWidth : containerWidth
  return resolution || null
}

const getRationHeightFromWidth = (width: number): number => (width * 9) / 16

const MAX_PLAYER_VIDEO_WIDTH = 900
const MAX_PLAYER_VIDEO_HEIGHT = getRationHeightFromWidth(MAX_PLAYER_VIDEO_WIDTH)
const DEFAULT_MAX_PLAYER_VIDEO_SIZE: Size = {
  width: MAX_PLAYER_VIDEO_WIDTH,
  height: MAX_PLAYER_VIDEO_HEIGHT,
}

const MediaPlayer: FC<Props> = ({ url, options, preview }) => {
  const playerRef = useRef<ReactPlayer>(null)
  const containerDimensions = useContext(DimensionsContext)
  const isClientSide = useIsClientSide()

  const isAudioMedia = options.format === MEDIA_FORMAT.AUDIO

  const [videoResolution, setVideoResolution] = useState<Size>(
    DEFAULT_MAX_PLAYER_VIDEO_SIZE
  )
  const hasLoop = false
  const volume = 1
  const [playerWidth, setPlayerWidth] = useState<number | undefined>(null)
  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const [played, setPlayed] = useState<number>(0)
  const [duration, setDuration] = useState<number>(0)
  const [maxVideoSize, setMaxVideoSize] = useState<Size>(
    DEFAULT_MAX_PLAYER_VIDEO_SIZE
  )

  const hasEnded = useMemo(() => played === 1, [played])

  const initResolution = useCallback(() => {
    if (options.format === MEDIA_FORMAT.VIDEO && playerRef.current) {
      // @ts-expect-error
      const videoNode = playerRef.current.wrapper
        .children[0] as HTMLVideoElement
      setVideoResolution({
        width: videoNode.videoWidth,
        height: videoNode.videoHeight,
      })
    }
  }, [playerRef.current])

  useEffect(() => {
    setPlayerWidth(getPlayerResolution(videoResolution, containerDimensions))
  }, [containerDimensions, videoResolution])

  useEffect(() => {
    if (videoResolution.width < DEFAULT_MAX_PLAYER_VIDEO_SIZE.width) {
      setMaxVideoSize(videoResolution)
    }
  }, [videoResolution])

  const handlePlayPause = useCallback(() => {
    setIsPlaying(!isPlaying)
  }, [isPlaying, played])

  const handlePlay = useCallback(() => {
    setIsPlaying(true)
  }, [])

  const handleProgress: BaseReactPlayerProps['onProgress'] = useCallback(
    (state) => {
      setPlayed(state.played)
    },
    []
  )
  const handleSeekChange = useCallback(
    (id: string, posX: number) => {
      const { left, width: max } = document
        .getElementById(id)
        .getBoundingClientRect()
      const x = posX - left
      const progress = x / max
      const value = progress
      setPlayed(value)
      if (playerRef.current) {
        playerRef.current.seekTo(value, 'fraction')
      }
    },
    [playerRef.current]
  )

  /*
  If ControlVolume is added to the media player controls

  Use this state instread of cont volume :
  const [volume, setVolume] = useState<number>(1)

  Pass this function in the ControlsVolume component's "onChange" parameter if ControlVolume is added to the media player controls:
  const handleVolumeChange = useCallback((e) => {
    setVolume(parseFloat(e.target.value))
  }, [])
  */

  const handleDuration: BaseReactPlayerProps['onDuration'] = useCallback(
    (duration) => {
      setDuration(duration)
    },
    []
  )

  const handleEnded = useCallback(() => {
    setIsPlaying(hasLoop)
  }, [])

  const canPlay = ReactPlayer.canPlay(url)
  if (!canPlay) {
    return <UnsupportedMediaMessage format={options.format} />
  }

  const playerHeight = useMemo(() => {
    if (playerWidth) return getRationHeightFromWidth(playerWidth)
  }, [playerWidth])

  if (isAudioMedia) {
    return (
      <>
        <div
          className="relative flex flex-col justify-center"
          style={{ width: playerWidth ? `${playerWidth}px` : '100%' }}
        >
          <div className="overlay [& > button]:bg-theme-primary-main [&:hover > button]:bg-theme-primary-main transition-[background 0.5s] absolute left-0 top-0 z-[2] flex h-full w-full flex-col items-center justify-center bg-[rgba(255,255,255,0.5)]">
            <ControlsPlay
              hasEnded={hasEnded}
              isPlaying={isPlaying}
              onClick={handlePlayPause}
            />
            <ControlsDuration duration={duration} played={played} />
          </div>
          {preview}
          {isClientSide && (
            <ReactPlayer
              ref={playerRef}
              controls={false}
              height={options.height}
              playing={isPlaying}
              url={url}
              volume={volume}
              width={options.width}
              onDuration={handleDuration}
              onEnded={handleEnded}
              onPlay={handlePlay}
              onProgress={handleProgress}
              onReady={initResolution}
            />
          )}
        </div>
        <ControlsTimeIndicator
          value={played * 100}
          onChange={handleSeekChange}
        />
      </>
    )
  }

  return (
    <div
      className="relative m-auto flex flex-col justify-center"
      style={{
        width: playerWidth ? `${playerWidth}px` : 'fit-content',
        height: playerHeight ? `${playerHeight}px` : 'auto',
        maxWidth: `${maxVideoSize.width}px`,
        maxHeight: `${maxVideoSize.height}px`,
      }}
    >
      <ReactPlayer
        ref={playerRef}
        controls
        height={options.height || '100%'}
        url={url}
        width={options.width || '100%'}
        onReady={initResolution}
      />
    </div>
  )
}

export default MediaPlayer
